import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import {
  BalanceRounded,
  CampaignOutlined,
  CloseRounded,
  DescriptionOutlined,
  ExpandMoreRounded,
  InfoOutlined,
  LockRounded,
  NorthEastRounded,
  OpenInNewRounded,
} from "@mui/icons-material";
import { ajustarTamanhoFonteAdmin } from "../estilos/global";

const LEI_PROCESSO_ADMINISTRATIVO =
  "https://www.almg.gov.br/legislacao-mineira/texto/LEI/14184/2002/?cons=1";

// Conteúdo de consulta: não registra aceite nem substitui declarações do processo.
const topicosTermos = [
  {
    id: "veracidade",
    titulo: "Veracidade das informações e documentos",
    paragrafos: [
      "O usuário é responsável pela veracidade, exatidão e integridade das informações prestadas no cadastro, nos formulários eletrônicos, requerimentos, relatórios e documentos encaminhados pelo SEAR.",
      "As informações devem corresponder à realidade dos fatos na data de sua apresentação, inclusive quanto às condições estruturais, administrativas, financeiras e pedagógicas declaradas da mantenedora e da instituição educacional. Os documentos devem ser autênticos e não conter adulterações.",
    ],
    referencia: {
      rotulo: "Referência legal",
      titulo: "Lei mineira nº 14.184/2002",
      descricao: "Art. 9º, I e II — verdade e boa-fé",
      url: LEI_PROCESSO_ADMINISTRATIVO,
    },
  },
  {
    id: "representacao",
    titulo: "Representação da instituição",
    paragrafos: [
      "Ao atuar em nome de uma entidade mantenedora ou instituição educacional, o usuário deve possuir os poderes necessários para os atos que praticar e apresentar a comprovação de representação quando exigida pelo procedimento aplicável.",
    ],
    referencia: {
      rotulo: "Referência legal",
      titulo: "Código Civil — arts. 115 a 118",
      descricao: "Poderes e limites da representação",
      url: "https://www2.camara.leg.br/legin/fed/lei/2002/lei-10406-10-janeiro-2002-432893-normaatualizada-pl.html",
    },
  },
  {
    id: "credenciais",
    titulo: "Credenciais e atos praticados",
    paragrafos: [
      "O usuário deve manter suas credenciais em sigilo, não compartilhá-las e comunicar prontamente qualquer suspeita de acesso indevido.",
      "É responsável pelos atos que praticar no sistema, observadas a apuração da autoria, as circunstâncias do caso e a legislação aplicável.",
    ],
    referencia: {
      rotulo: "Regra de segurança do SEAR",
      descricao: "Uso pessoal e proteção das credenciais de acesso.",
    },
  },
  {
    id: "apuracao",
    titulo: "Apuração e responsabilização",
    paragrafos: [
      "A apresentação de informações falsas, documentos falsificados ou adulterados, bem como o uso de documento falso, poderá dar origem à apuração pelos órgãos competentes e à responsabilização administrativa, civil e penal cabível, conforme a conduta e a legislação aplicável, assegurados os direitos de defesa.",
      "A apresentação ou a análise do material no SEAR não afasta a responsabilidade de seu autor.",
    ],
    referencia: {
      rotulo: "Referências penais, conforme a conduta",
      titulo: "Código Penal — Decreto-Lei nº 2.848/1940",
      url: "https://www2.camara.leg.br/legin/fed/declei/1940-1949/decreto-lei-2848-7-dezembro-1940-412868-normaatualizada-pe.html",
      artigos: [
        ["Art. 297", "falsificação de documento público."],
        ["Art. 298", "falsificação de documento particular."],
        ["Art. 299", "falsidade ideológica."],
        ["Art. 304", "uso de documento falso."],
      ],
      observacao:
        "O enquadramento depende dos requisitos legais e da apuração de cada caso.",
    },
  },

];

const textoTopico = {
  fontSize: ajustarTamanhoFonteAdmin("12px"),
  lineHeight: 1.7,
  color: "inherit",
};
const textoSistema = {
  ...textoTopico,
  fontSize: ajustarTamanhoFonteAdmin("13px"),
  lineHeight: 1.7,
};
const tituloBloco = {
  fontSize: ajustarTamanhoFonteAdmin("12px"),
  fontWeight: 600,
  mb: 0.875,
};
const linkExterno = { target: "_blank", rel: "noopener noreferrer" };
const estiloTopico = {
  borderBottom: "1px solid",
  borderColor: "divider",
  "& > summary": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1.5,
    py: 1.5,
    listStyle: "none",
    cursor: "pointer",
    fontWeight: 500,
    color: "inherit",
    fontSize: ajustarTamanhoFonteAdmin("13px"),
    "&::-webkit-details-marker": { display: "none" },
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: 2,
    },
  },
  "&[open] > summary > svg": { transform: "rotate(180deg)" },
};

function ReferenciaLegal({ referencia }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 0.75,
        p: 1.25,
        borderRadius: 1,
        borderLeft: "2px solid",
        borderColor: "primary.main",
        bgcolor: "var(--sear-superficie-azul, #edf6ff)",
      }}
    >
      <Typography
        sx={{
          fontSize: ajustarTamanhoFonteAdmin("11px"),
          fontWeight: 500,
          color: "inherit",
        }}
      >
        {referencia.rotulo}
      </Typography>
      {referencia.url ? (
        <Link
          href={referencia.url}
          {...linkExterno}
          underline="hover"
          sx={{
            ...textoTopico,
            color: "primary.main",
            overflowWrap: "anywhere",
          }}
        >
          {referencia.titulo}
          {referencia.descricao && (
            <Box component="span" sx={{ display: "block" }}>
              {referencia.descricao}
            </Box>
          )}
          <OpenInNewRounded
            sx={{ fontSize: 12, ml: 0.5, verticalAlign: "middle" }}
          />
        </Link>
      ) : (
        <Typography sx={textoTopico}>{referencia.descricao}</Typography>
      )}
      {referencia.artigos && (
        <Box component="ul" sx={{ m: 0, pl: 2, display: "grid", gap: 0.5 }}>
          {referencia.artigos.map(([artigo, descricao]) => (
            <Box component="li" key={artigo} sx={textoTopico}>
              <Box
                component="strong"
                sx={{ fontWeight: 600, color: "inherit" }}
              >
                {artigo}
              </Box>{" "}
              — {descricao}
            </Box>
          ))}
        </Box>
      )}
      {referencia.observacao && (
        <Typography sx={textoTopico}>{referencia.observacao}</Typography>
      )}
    </Box>
  );
}

function TermosResponsabilidades() {
  return topicosTermos.map((topico, indice) => (
    <Box
      component="details"
      key={topico.id}
      open={indice === 0 ? true : undefined}
      sx={estiloTopico}
    >
      <Box component="summary">
        <Box component="span">{topico.titulo}</Box>
        <ExpandMoreRounded
          sx={{ fontSize: 18, flexShrink: 0, color: "primary.main" }}
        />
      </Box>
      <Box sx={{ display: "grid", gap: 1.25, pb: 1.75 }}>
        {topico.paragrafos.map((texto, posicao) => (
          <Typography key={posicao} sx={textoTopico}>
            {texto}
          </Typography>
        ))}
        <ReferenciaLegal referencia={topico.referencia} />
      </Box>
    </Box>
  ));
}

function InformacoesSistema({ onAbrirNovidades, novidadesEmDesenvolvimento }) {
  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box
        sx={{ pl: 1.75, borderLeft: "3px solid", borderColor: "primary.main" }}
      >
        <Typography component="h3" sx={tituloBloco}>
          Objetivo
        </Typography>
        <Typography sx={textoSistema}>
          Contribuir para a regularidade do funcionamento das instituições de
          Educação Básica e de suas ofertas educacionais, apoiando a
          Superintendência Regional de Ensino na gestão dos processos previstos
          na legislação educacional.
        </Typography>
      </Box>
      <Box>
        <Typography component="h3" sx={tituloBloco}>
          Atendimento remoto
        </Typography>
        <Typography sx={textoSistema}>
          Por meio do atendimento remoto, o SEAR facilita o encaminhamento de
          solicitações, a instrução documental, a análise e o acompanhamento dos
          processos pelas mantenedoras, instituições e equipes responsáveis,
          observadas as competências dos órgãos envolvidos.
        </Typography>
      </Box>
      <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
        <Box component="details" sx={estiloTopico}>
          <Box component="summary">
            <Box
              component="span"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <BalanceRounded sx={{ fontSize: 16, color: "primary.main" }} />
              Referências normativas
            </Box>
            <ExpandMoreRounded
              sx={{ fontSize: 18, flexShrink: 0, color: "primary.main" }}
            />
          </Box>
          <Box sx={{ display: "grid", gap: 1, pb: 1.5 }}>
            <Link
              href="https://www2.camara.leg.br/legin/fed/lei/1996/lei-9394-20-dezembro-1996-362578-normaatualizada-pl.html"
              {...linkExterno}
              sx={{ fontSize: ajustarTamanhoFonteAdmin("12px") }}
            >
              Lei nº 9.394/1996 — LDB, arts. 7º e 10{" "}
              <OpenInNewRounded
                sx={{ fontSize: 12, verticalAlign: "middle" }}
              />
            </Link>
            <Link
              href="https://www.educacao.mg.gov.br/wp-content/uploads/2025/10/Resolucao-CEE-496-de-21-de-fevereiro-de-2024.pdf"
              {...linkExterno}
              sx={{ fontSize: ajustarTamanhoFonteAdmin("12px") }}
            >
              Resolução CEE nº 496, de 21/12/2023{" "}
              <OpenInNewRounded
                sx={{ fontSize: 12, verticalAlign: "middle" }}
              />
            </Link>
          </Box>
        </Box>
        <ButtonBase
          type="button"
          onClick={novidadesEmDesenvolvimento ? undefined : onAbrirNovidades}
          aria-haspopup={novidadesEmDesenvolvimento ? undefined : "dialog"}
          aria-disabled={novidadesEmDesenvolvimento || undefined}
          aria-label={
            novidadesEmDesenvolvimento
              ? "Consulte as Novidades do SEAR em desenvolvimento"
              : "Consulte as Novidades do SEAR"
          }
          disableRipple={novidadesEmDesenvolvimento}
          sx={{
            ...textoTopico,
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            py: 1.5,
            textAlign: "left",
            fontWeight: 600,
            color: novidadesEmDesenvolvimento
              ? "text.disabled"
              : "primary.main",
            borderRadius: 0.5,
            cursor: novidadesEmDesenvolvimento ? "not-allowed" : "pointer",
            "&:hover": {
              textDecoration: novidadesEmDesenvolvimento ? "none" : "underline",
            },
            "&.Mui-focusVisible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 3,
            },
          }}
        >
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CampaignOutlined sx={{ fontSize: 16 }} />
            Consulte as Novidades do SEAR
          </Box>

          {novidadesEmDesenvolvimento ? (
            <LockRounded sx={{ fontSize: 16, flexShrink: 0 }} />
          ) : (
            <NorthEastRounded sx={{ fontSize: 16, flexShrink: 0 }} />
          )}
        </ButtonBase>
      </Box>
    </Box>
  );
}

export default function SobreSearDialog({
  open,
  onClose,
  onAbrirNovidades,
  novidadesEmDesenvolvimento = false,
}) {
  const escuro = useTheme().palette.mode === "dark";
  const [secao, setSecao] = useState("sistema");

  useEffect(() => {
    if (!open) setSecao("sistema");
  }, [open]);

  const secoes = [
    {
      id: "sistema",
      titulo: "O sistema",
      icone: <InfoOutlined sx={{ fontSize: 17 }} />,
    },
    {
      id: "termos",
      titulo: "Termos e responsabilidades",
      icone: <DescriptionOutlined sx={{ fontSize: 17 }} />,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      aria-labelledby="sobre-sear-titulo"
      PaperProps={{
        sx: {
          width: 660,
          maxWidth: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" },
          m: { xs: 1.5, sm: 3 },
          maxHeight: "calc(100dvh - 32px)",
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderTop: "3px solid",
          borderTopColor: "primary.main",
          bgcolor: escuro ? "background.paper" : "#fff",
          color: escuro ? "text.primary" : "#18365d",
          boxShadow: escuro
            ? "0 10px 30px rgba(0,0,0,.3)"
            : "0 10px 30px rgba(16,52,89,.14)",
        },
      }}
    >
      <DialogTitle
        id="sobre-sear-titulo"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: { xs: 2, sm: 2.5 },
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <InfoOutlined color="primary" sx={{ fontSize: 20 }} />
        <Box
          component="span"
          sx={{
            flex: 1,
            fontSize: ajustarTamanhoFonteAdmin("16px"),
            fontWeight: 600,
          }}
        >
          Sobre o SEAR
        </Box>
        <IconButton
          size="small"
          aria-label="Fechar Sobre o SEAR"
          onClick={onClose}
        >
          <CloseRounded sx={{ fontSize: 19 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            m: { xs: 2, sm: 2.5 },
            mb: 0,
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.75,
            borderRadius: 1,
            background: escuro
              ? "linear-gradient(110deg, #193c60, #24384f)"
              : "linear-gradient(110deg, #2ea5fb 0%, #9bd6ff 25%, #e1effd 65%, #e1effd 100%)",
            color: "#fff",
          }}
        >
          <Typography
            sx={{
              fontSize: ajustarTamanhoFonteAdmin("27px"),
              fontWeight: 800,
              letterSpacing: "-.03em",
              lineHeight: 1.2,
              color: "#fff",
            }}
          >
            SEAR
          </Typography>
          <Typography
            sx={{
              fontSize: ajustarTamanhoFonteAdmin("13px"),
              lineHeight: 1.45,
              color: "#fff",
            }}
          >
            Sistema Eletrônico <br />
            de Atendimento Remoto
          </Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Box
            component="nav"
            aria-label="Informações do SEAR"
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: { xs: 1.5, sm: 2.25 },
              px: { xs: 2, sm: 2.5 },
              mb: secao === "sistema" ? 1.5 : 2.5,
              alignItems: "stretch",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            {secoes.map(({ id, titulo, icone }) => (
              <ButtonBase
                key={id}
                type="button"
                aria-pressed={secao === id}
                aria-controls="sobre-sear-conteudo"
                onClick={() => setSecao(id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 0.875,
                  px: 0.25,
                  py: 1.25,
                  borderRadius: 0,
                  textAlign: "left",
                  minWidth: 0,
                  flex: { xs: 1, sm: "initial" },
                  fontSize: ajustarTamanhoFonteAdmin("13px"),
                  lineHeight: 1.5,
                  fontWeight: secao === id ? 500 : 400,
                  color: secao === id ? "primary.main" : "inherit",
                  bgcolor: "transparent",
                  boxShadow:
                    secao === id ? "inset 0 -2px currentColor" : "none",
                  "&.Mui-focusVisible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 2,
                  },
                }}
              >
                {icone}
                <Box component="span">{titulo}</Box>
              </ButtonBase>
            ))}
          </Box>
          <Box
            component="section"
            id="sobre-sear-conteudo"
            aria-labelledby="sobre-sear-secao-titulo"
            sx={{
              px: { xs: 2, sm: 3 },
              pb: secao === "sistema" ? 1.5 : 2.5,
              minWidth: 0,
              overflowWrap: "anywhere",
            }}
          >
            <Typography
              component="h2"
              id="sobre-sear-secao-titulo"
              sx={{
                mb: 1.5,
                fontSize: ajustarTamanhoFonteAdmin("17px"),
                lineHeight: 1.4,
                fontWeight: 600,
                ...(secao === "sistema" && {
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  p: 0,
                  m: -1,
                  overflow: "hidden",
                  clip: "rect(0, 0, 0, 0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }),
              }}
            >
              {secao === "sistema" ? "O sistema" : "Termos e responsabilidades"}
            </Typography>

            {secao === "sistema" ? (
              <InformacoesSistema
                onAbrirNovidades={onAbrirNovidades}
                novidadesEmDesenvolvimento={novidadesEmDesenvolvimento}
              />
            ) : (
              <TermosResponsabilidades />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.25,
          justifyContent: "space-between",
          gap: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          component="a"
          href="https://sreuberlandia.educacao.mg.gov.br/"
          {...linkExterno}
          size="small"
          endIcon={<OpenInNewRounded sx={{ fontSize: "14px !important" }} />}
          sx={{
            fontSize: ajustarTamanhoFonteAdmin("12px"),
            textTransform: "none",
          }}
        >
          Página da SRE
        </Button>
        <Button
          onClick={onClose}
          size="small"
          variant="contained"
          disableElevation
          sx={{
            px: 1.75,
            fontSize: ajustarTamanhoFonteAdmin("13px"),
            textTransform: "none",
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
